// src/pages/About/index.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Layout, Typography, Card, Space, Avatar, Descriptions, Button, Form, Input, Modal, message, Upload, Spin } from 'antd';
import { GithubOutlined, LinkedinOutlined, RocketOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import AppHeader from '../../components/Header';
import AppFooter from '../../components/Footer';
import './index.css';

const { Content } = Layout;
const { Title, Paragraph, Link: AntLink } = Typography;
const { TextArea } = Input;

const About = ({ aboutInfo, onUpdateAboutInfo, loading, canEdit, loggedInUser, onLogout }) => { 
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (editModalVisible) {
            form.setFieldsValue(aboutInfo);
        }
    }, [aboutInfo, editModalVisible, form]);


    const handleCancelEdit = () => {
        setEditModalVisible(false);
        form.resetFields();
    };

    const handleSaveEdit = async () => {
        try {
            const values = await form.validateFields();
            const updatedInfo = {
                ...aboutInfo, 
                ...values,
                avatar: values.avatar || '',
            };
            onUpdateAboutInfo(updatedInfo);
            setEditModalVisible(false);
        } catch (error) {
            console.error('Failed to save about info:', error);
            message.error('保存失败，请检查表单内容。');
        }
    };

    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            message.error('你只能上传 JPG/PNG 文件!');
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error('图片必须小于 2MB!');
        }
        return isJpgOrPng && isLt2M;
    };
    if (loading) {
        return (
            <Layout className="blog-layout">
                <AppHeader loggedInUser={loggedInUser} onLogout={onLogout} /> 
                <Content className="blog-content about-content">
                    <div className="loading-container">
                        <Spin size="large" tip="正在加载个人信息..." />
                    </div>
                </Content>
                <AppFooter />
            </Layout>
        );
    }
    return (
        <Layout className="blog-layout">
            <AppHeader loggedInUser={loggedInUser} onLogout={onLogout} />
            <Content className="blog-content about-content">
                <div className="about-container">
                    <Card className="about-card" bordered={false}>
                        <div className="profile-section">
                            <Avatar size={120} src={aboutInfo.avatar} />
                            <Title level={2} className="about-title">Hi，我是 {aboutInfo.name}！</Title>
                            <Paragraph className="about-intro">
                                {aboutInfo.intro}
                            </Paragraph>
                        </div>

                        <Paragraph className="about-details">
                            {aboutInfo.details}
                        </Paragraph>

                        <Descriptions bordered column={1} className="contact-info">
                            <Descriptions.Item label="邮箱">{aboutInfo.email}</Descriptions.Item>
                            <Descriptions.Item label="技术栈">{aboutInfo.techStack}</Descriptions.Item>
                            <Descriptions.Item label="兴趣爱好">{aboutInfo.interests}</Descriptions.Item>
                        </Descriptions>

                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            style={{ marginTop: 20 }}
                            onClick={() => {
                                if (canEdit) {
                                  form.setFieldsValue(aboutInfo);
                                  setEditModalVisible(true);
                                } else {
                                  message.warn('请先登录才能编辑个人信息哦！');
                                }
                            }}
                            disabled={!canEdit}
                        >
                            编辑个人信息
                        </Button>
                    </Card>
                </div>
            </Content>
            <AppFooter />

            <Modal
                title="编辑个人信息"
                open={editModalVisible}
                onOk={handleSaveEdit}
                onCancel={handleCancelEdit}
                maskClosable={false}
                destroyOnClose={true}
                centered
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="edit_about_info"
                    initialValues={aboutInfo}
                >
                    <Form.Item
                        name="avatar"
                        label="头像 (URL)"
                        rules={[{ required: true, message: '请输入头像URL！' }]}
                    >
                        <Input placeholder="请输入头像图片的URL地址" />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="姓名"
                        rules={[{ required: true, message: '请输入您的姓名！' }]}
                    >
                        <Input placeholder="您的姓名" />
                    </Form.Item>

                    <Form.Item
                        name="intro"
                        label="个人简介"
                        rules={[{ required: true, message: '请输入个人简介！' }]}
                    >
                        <TextArea rows={4} placeholder="简短介绍您自己" />
                    </Form.Item>

                    <Form.Item
                        name="details"
                        label="更详细的介绍"
                        rules={[{ required: true, message: '请输入详细介绍！' }]}
                    >
                        <TextArea rows={6} placeholder="详细介绍您的经历、理念等" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="联系邮箱"
                        rules={[{ required: true, type: 'email', message: '请输入有效的邮箱！' }]}
                    >
                        <Input placeholder="您的邮箱" />
                    </Form.Item>

                    <Form.Item name="techStack" label="技术栈">
                        <Input placeholder="您擅长的技术栈，如 React, Vue, Node.js" />
                    </Form.Item>

                    <Form.Item name="interests" label="兴趣爱好">
                        <Input placeholder="您的兴趣爱好" />
                    </Form.Item>

                    <Form.Item name="github" label="GitHub 链接">
                        <Input placeholder="您的 GitHub 主页链接" />
                    </Form.Item>

                    <Form.Item name="personalHomepage" label="个人主页链接 (可选)">
                        <Input placeholder="您的个人网站/博客链接" />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default About;
